import { request } from "../../utils/request";

// ==================== Types ====================

export interface PodcastRoundRecord {
  roundId: number;
  speaker: string;
  text: string;
  durationMs: number;
  startTime: number;
  endTime: number;
}

export interface NlpTextItem {
  speaker: string;
  text: string;
}

export interface PodcastSpeaker {
  speakerId: string;
  speakerName: string;
  gender: string;
}

export type PodcastSpeakerLike = string | PodcastSpeaker;

export interface PodcastTaskRespVO {
  id: number;
  taskId: string;
  name?: string;
  userId: number;
  action: number;
  inputText: string;
  inputUrl: string;
  inputFileUrl?: string;
  inputFileName?: string;
  inputFileSizeBytes?: number;
  promptText: string;
  nlpTexts: NlpTextItem[];
  speakers: PodcastSpeakerLike[];
  audioFormat: string;
  sampleRate: number;
  speechRate: number;
  useHeadMusic: boolean;
  useTailMusic: boolean;
  status: number; // 0=待生成 1=生成中 2=成功 3=失败
  statusText: string;
  audioUrl: string;
  coverUrl?: string;
  audioDurationMs?: number;
  audioDurationSec?: number;
  isTemplate?: boolean;
  viewCount?: number;
  audioSizeBytes: number;
  rounds: PodcastRoundRecord[];
  srtUrl: string;
  scriptUrl: string;
  srtContent: string;
  scriptContent: string;
  errorCode: string;
  errorMsg: string;
  createTime: string;
  finishTime: string;
}

export interface PodcastPageResult {
  list: PodcastTaskRespVO[];
  total: number;
}

export interface PodcastGenerateReq {
  action: number; // 0=长文/URL 3=多轮对话 4=prompt联网
  inputText?: string;
  inputUrl?: string;
  nlpTexts?: Array<{ speaker: string; text: string }>;
  promptText?: string;
  speakers: string[]; // 必须恰好2个
  audioFormat?: string;
  sampleRate?: number;
  speechRate?: number;
  useHeadMusic?: boolean;
  useTailMusic?: boolean;
  strictAudit?: boolean;
  aigcWatermark?: boolean;
  maxCharLengthPerRound?: number;
}

// ==================== API ====================

/** 异步提交播客生成（JSON，无文件），立即返回 taskId */
export const podcastGenerateAsyncApi = (body: PodcastGenerateReq) =>
  request<{ code: number; data: PodcastTaskRespVO; msg: string }>("/video/podcast/generate-async", {
    method: "POST",
    body,
  });

/** 异步提交播客生成（multipart，可选文件），立即返回 taskId */
export const podcastGenerateAsyncWithFileApi = (reqJson: string, file?: File) => {
  const formData = new FormData();
  formData.append("req", new Blob([reqJson], { type: "application/json" }));
  if (file) formData.append("file", file);
  return request<{ code: number; data: PodcastTaskRespVO; msg: string }>("/video/podcast/generate-async", {
    method: "POST",
    body: formData,
    skipAuthRedirect: true,
  });
};

/** 长轮询等待任务完成 */
export const podcastWaitTaskApi = (taskId: string, timeoutSeconds = 60) =>
  request<{ code: number; data: PodcastTaskRespVO; msg: string }>(
    `/video/podcast/${taskId}/wait`,
    { method: "GET", params: { timeoutSeconds } }
  );

/** 分页查询我的播客任务（keyword 可选，模糊匹配播客名称 + 音色名） */
export const podcastPageApi = (pageNo: number, pageSize: number, keyword?: string) =>
  request<{ code: number; data: PodcastPageResult; msg: string }>("/video/podcast/page", {
    method: "GET",
    params: { pageNo, pageSize, ...(keyword ? { keyword } : {}) },
  });

/** 查询单个播客任务详情 */
export const podcastTaskDetailApi = (taskId: string) =>
  request<{ code: number; data: PodcastTaskRespVO; msg: string }>(`/video/podcast/${taskId}`, {
    method: "GET",
  });

/** 删除播客任务 */
export const podcastDeleteApi = (taskId: string) =>
  request<{ code: number; data: boolean; msg: string }>(`/video/podcast/${taskId}`, {
    method: "DELETE",
  });



/** 查询模板任务列表 */
export const podcastTemplatesApi = () =>
  request<{ code: number; data: PodcastTaskRespVO[]; msg: string }>("/video/podcast/templates", {
    method: "GET",
  });

/** 播客公共音色列表 */
export const podcastVoicesApi = (params?: { displayName?: string; gender?: string; scene?: string; provider?: string; sourceType?: string }) =>
  request<{ code: number; data: VoiceProfileListResp[]; msg: string }>("/voice-performance/voices", {
    method: "GET",
    params,
  });

export interface VoiceProfileListResp {
  id: number;
  speakerId?: string;
  displayName: string;
  description?: string;
  provider: string;
  sourceType: string;
  gender: string;
  scene: string;
  avatarUrl: string;
  previewAudioUrl: string;
}
